import { Component, OnInit, HostListener } from '@angular/core';
import { MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { TranslateService } from '@ngx-translate/core';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { RegistrationService } from '../../../../../lib/src/api/services';
import { CacheService, AccountService, FOYDataService, LanguageEnum, RegistrationStatus, AuthResponse } from '../../../../../lib/src/public-api';
import { of } from 'rxjs';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false
})
export class RegistrationComponent implements OnInit {

  isLegalAccepted: boolean = false;
  registerClicked = false;
  registrationForm: FormGroup;
  isRegistrationPending = false;
  genderOptions = [
    "Male",
    "Female",
    "Transgender",
    "Other"
  ];

  constructor(
    private registrationService: RegistrationService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private router: Router,
    private translate: TranslateService,
    private cacheService: CacheService,
    private accountService: AccountService,
    private route: ActivatedRoute,
    private foyDataService: FOYDataService
  ) { }

  ngOnInit() {
    this.registrationForm = this.fb.group({
      company: ['', Validators.required],
      selfDescription: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', Validators.required],
      fax: [''],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
      eventInvitations: [null as boolean | null, Validators.required],
      ideasAndInsights: [null as boolean | null, Validators.required],
      generalMarketing: [null as boolean | null, Validators.required],
      industryRelevantSurveys: [null as boolean | null, Validators.required],
    });

    var form = this.cacheService.getRegistrationFormData();
    if (form) {
      this.registrationForm.patchValue(form);
    }
  }

  public ngOnDestroy(): void {
    this.cacheService.setRegistrationFormData(this.registrationForm.value);
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: any) {
    if (event.keyCode === 39 && this.canRegister()) {
      this.buttonRegisterClicked();
    }
  }

  legalAccepted(event : CheckboxChangeEvent) {
    var value = event.checked;
    this.isLegalAccepted = value;
  }

  canRegister() {
    return this.registrationForm.valid && this.registrationForm.controls.password.value === this.registrationForm.controls.confirmPassword.value;
  }

  buttonRegisterClicked() {
    var language = this.translate.currentLang;

    var email = this.registrationForm.controls.email.value;
    var password = this.registrationForm.controls.password.value;
    this.isRegistrationPending = true;

    this.registrationService.apiRegistrationPost$Json({body: {
      company: this.registrationForm.controls.company.value,
      email: email,
      fax: this.registrationForm.controls.fax.value,
      firstName: this.registrationForm.controls.firstName.value,
      lastName: this.registrationForm.controls.lastName.value,
      password: password,
      phone: this.registrationForm.controls.phone.value,
      selfDescription: this.registrationForm.controls.selfDescription.value,
      language: language == "de" ? LanguageEnum.German : LanguageEnum.English,
      eventInvitations: this.registrationForm.controls.eventInvitations.value,
      ideasAndInsights: this.registrationForm.controls.ideasAndInsights.value,
      generalMarketing: this.registrationForm.controls.generalMarketing.value,
      industryRelevantSurveys: this.registrationForm.controls.industryRelevantSurveys.value
    }}).pipe(
        switchMap(data => {
          if (data == RegistrationStatus.Success) {
            this.registerClicked = true;
            return this.accountService.login(email, password).pipe(tap(_ => this.foyDataService.init()));
          }
          return of(data);
        }),
          finalize(() => this.isRegistrationPending = false)
      )
      .subscribe(data => {
        if (data == null) {
          return;
        }
        var isRegistrationStatus = typeof data === 'number';
        if (isRegistrationStatus) {
          if (data == RegistrationStatus.UserAlreadyRegistered) {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('User Already Registered'), sticky: false });
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('Registration error'), sticky: false });
          }
        } else {
          var authResponse = data as AuthResponse;
          if (authResponse != null && 'errorMessage' in authResponse && authResponse.errorMessage == null) {
            this.registerClicked = true;
            const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
            this.router.navigateByUrl(returnUrl);
          }
        }
      }, _ => {
        this.messageService.clear();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translate.instant('Registration exception'), sticky: false });
      });
  }
}
